import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
  Typography
} from '@mui/material';
import plusIcon from '../../assets/icons/plus.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import checkIcon from '../../assets/icons/check.svg';
import { useAuth } from '../../context/AuthContext';
import { withdrawalRequestAPI, caisseAPI, userAPI, dashboardAPI, categoryAPI } from '../../services/api';
import { isWithdrawalCategory } from '../../utils/categoryUtils';
import WithdrawalRequestFilters from './WithdrawalRequestFilters';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import Rows from '../../components/ui/Rows';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import StatusPill from '../../components/ui/StatusPill';
import CreationModal from '../../components/ui/CreationModal';

const WithdrawalRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [caisses, setCaisses] = useState([]);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0
  });
  const [selectedRequests, setSelectedRequests] = useState([]);
  
  const [cancelDialog, setCancelDialog] = useState({ open: false, requestId: null, requestCode: null });
  const [validateDialog, setValidateDialog] = useState({ open: false, requestId: null, requestCode: null, amount: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, requestId: null, requestCode: null, amount: null });
  const [bulkCancelDialog, setBulkCancelDialog] = useState({ open: false, selectedIds: [] });
  const [bulkValidateDialog, setBulkValidateDialog] = useState({ open: false, selectedIds: [] });
  const [createModal, setCreateModal] = useState({ open: false });
  const [createConfirmDialog, setCreateConfirmDialog] = useState({ 
    open: false, 
    formData: null 
  });
  const [actionLoading, setActionLoading] = useState(false);
  
  // Success/Error feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Sticky header state
  const [isStuck, setIsStuck] = useState(false);

  const STICKY_TOP_OFFSET = 76;

  // Handle scroll to update isStuck state
  useEffect(() => {
    const handleScroll = () => {
      setIsStuck(window.scrollY > STICKY_TOP_OFFSET);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [filters, setFilters] = useState({
    searchTerm: '',
    statusIds: [],
    caisseIds: [],
    assignedToIds: [],
    createdByIds: [],
    dateRange: null
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('DESC');

  // Fetch coffre balance for a specific caisse
  const fetchCoffreBalance = useCallback(async (caisseId = null) => {
    try {
      const response = await dashboardAPI.getAllCaissesDashboard();
      if (response.data?.caisses?.length > 0) {
        let coffreBalance = 0;
        
        if (caisseId) {
          // Get coffre balance for specific caisse
          const caisseData = response.data.caisses.find(caisse => caisse.id === caisseId);
          coffreBalance = caisseData?.coffreBalance || 0;
        } else {
          // Get coffre balance from first caisse (fallback)
          coffreBalance = response.data.caisses[0].coffreBalance || 0;
        }
        
        return coffreBalance; // Return the fresh balance
      }
      return 0;
    } catch (error) {
      console.error('Error fetching coffre balance:', error);
      return 0;
    }
  }, []);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: sortBy,
        sortDirection: sortDirection,
        searchTerm: filters.searchTerm,
        // Convert date range to backend format (avoid timezone issues)
        fromDate: filters.dateRange?.start ? filters.dateRange.start.toLocaleDateString('en-CA') : undefined,
        toDate: filters.dateRange?.end ? filters.dateRange.end.toLocaleDateString('en-CA') : undefined,
      };

      // Add multi-select parameters - axios will handle array serialization
      if (filters.statusIds && filters.statusIds.length > 0) {
        params.statusIds = filters.statusIds;
      }
      
      // For MANAGER users, send all filter parameters
      if (user?.role === 'MANAGER') {
        if (filters.caisseIds && filters.caisseIds.length > 0) {
          params.caisseIds = filters.caisseIds;
        }
        
        if (filters.assignedToIds && filters.assignedToIds.length > 0) {
          params.assignedToIds = filters.assignedToIds;
        }
        
        if (filters.createdByIds && filters.createdByIds.length > 0) {
          params.createdByIds = filters.createdByIds;
        }
      } else {
        // For AGENT users, send createdByIds (caisse and assignedTo are handled by backend)
        if (filters.createdByIds && filters.createdByIds.length > 0) {
          params.createdByIds = filters.createdByIds;
        }
      }

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined || 
            (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      // Use different endpoints based on user role
      const response = user?.role === 'AGENT' 
        ? await withdrawalRequestAPI.getForAgent(params)
        : await withdrawalRequestAPI.getAll(params);
      setRequests(response.data.content || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.totalElements || 0
      }));
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Échec du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.size, sortBy, sortDirection, user?.role]);

  // Load filter options (only for MANAGER users)
  useEffect(() => {
    if (user?.role !== 'MANAGER') {
      return; // Skip loading filter options for AGENT users
    }
    
    const loadFilterOptions = async () => {
      try {
        const [caissesRes, usersRes] = await Promise.all([
          caisseAPI.getAll({ active: true }),
          userAPI.getAssignableUsers()
        ]);
        const allCaisses = caissesRes.data.content || [];

        // Filter caisses to only those with an active "Sortie Coffre" category
        // Fetch categories once and group per caisse
        const categoriesRes = await categoryAPI.getAll({ size: 10000 });
        const allCategories = (categoriesRes.data?.content || []).filter(Boolean);

        const caisseIdToHasActiveWithdrawal = new Map();
        for (const cat of allCategories) {
          if (isWithdrawalCategory(cat) && cat.active && cat.caisse?.id) {
            caisseIdToHasActiveWithdrawal.set(cat.caisse.id, true);
          }
        }

        const filteredCaisses = allCaisses.filter(c => caisseIdToHasActiveWithdrawal.get(c.id));

        setCaisses(filteredCaisses);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };

    loadFilterOptions();
  }, [user?.role]);

  // Load requests when filters or pagination change
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Container styling matching Transactions page
  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  const stickyOuterSx = { ...contentContainerSx };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page
    setSelectedRequests([]); // Clear selections when filters change
  };

  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      statusIds: [],
      caisseIds: [],
      assignedToIds: [],
      createdByIds: [],
      dateRange: null
    });
    setPagination(prev => ({ ...prev, page: 0 }));
    setSelectedRequests([]);
  };

  // Sorting handler
  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
  };



  const handleCancelRequest = (requestId, requestCode) => {
    setCancelDialog({ open: true, requestId, requestCode });
  };

  const handleValidateRequest = (requestId, requestCode, amount) => {
    setValidateDialog({ open: true, requestId, requestCode, amount });
  };

  const handleConfirmRequest = (requestId, requestCode, amount) => {
    setConfirmDialog({ open: true, requestId, requestCode, amount });
  };

  const handleConfirmCancel = async () => {
    if (!cancelDialog.requestId) return;
    
    setActionLoading(true);
    try {
      await withdrawalRequestAPI.cancel(cancelDialog.requestId);
      setSnackbar({ open: true, message: 'Demande annulée avec succès', severity: 'success' });
      setCancelDialog({ open: false, requestId: null, requestCode: null });
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error cancelling request:', err);
      setSnackbar({ open: true, message: 'Erreur lors de l\'annulation de la demande', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmValidate = async () => {
    if (!validateDialog.requestId) return;
    
    setActionLoading(true);
    try {
      await withdrawalRequestAPI.validate(validateDialog.requestId);
      setSnackbar({ open: true, message: 'Demande validée avec succès. Transaction Sortie Coffre créée.', severity: 'success' });
      setValidateDialog({ open: false, requestId: null, requestCode: null, amount: null });
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error validating request:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la validation de la demande', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmConfirm = async () => {
    if (!confirmDialog.requestId) return;
    
    setActionLoading(true);
    try {
      await withdrawalRequestAPI.confirm(confirmDialog.requestId);
      setSnackbar({ open: true, message: 'Demande confirmée avec succès', severity: 'success' });
      setConfirmDialog({ open: false, requestId: null, requestCode: null, amount: null });
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error confirming request:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la confirmation de la demande', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Smart checkbox behavior - allow selection of OPEN and CONFIRMED requests
  const canSelectRequest = (request) => {
    const status = request.originalData?.status;
    return status === 'OPEN' || status === 'CONFIRMED';
  };

  // Smart bulk cancellation handler - only cancels OPEN requests created by current user
  const handleBulkCancel = () => {
    if (selectedRequests.length === 0) return;
    // Only cancel OPEN requests from selection that the user created
    const openRequests = selectedRequests.filter(id => {
      const request = transformedRequests.find(r => r.id === id);
      return request?.originalData?.status === 'OPEN' && 
             request?.originalData?.requestedById === user?.id;
    });
    
    if (openRequests.length === 0) {
      setSnackbar({ 
        open: true, 
        message: 'Aucune demande ouverte créée par vous sélectionnée pour annulation', 
        severity: 'warning' 
      });
      return;
    }
    
    setBulkCancelDialog({ open: true, selectedIds: openRequests });
  };

  // Smart bulk validation handler - only validates CONFIRMED requests created by current user
  const handleBulkValidate = () => {
    if (selectedRequests.length === 0) return;
    // Only validate CONFIRMED requests from selection that the user created
    const confirmedRequests = selectedRequests.filter(id => {
      const request = transformedRequests.find(r => r.id === id);
      return request?.originalData?.status === 'CONFIRMED' && 
             request?.originalData?.requestedById === user?.id;
    });
    
    if (confirmedRequests.length === 0) {
      setSnackbar({ 
        open: true, 
        message: 'Aucune demande confirmée créée par vous sélectionnée pour validation', 
        severity: 'warning' 
      });
      return;
    }
    
    setBulkValidateDialog({ open: true, selectedIds: confirmedRequests });
  };

  const handleCreateRequest = () => {
    setCreateModal({ open: true });
  };

  const handleSubmitCreateRequest = async (formData) => {
    // Fetch coffre balance for the selected caisse and get fresh balance
    const freshCoffreBalance = await fetchCoffreBalance(formData.caisse);
    
    // Validate amount against fresh coffre balance
    const requestedAmount = parseFloat(formData.amount);
    if (requestedAmount > freshCoffreBalance) {
      setSnackbar({
        open: true,
        message: `Le montant demandé (${formatAmountForDisplay(requestedAmount, false).text}) dépasse le solde coffre disponible (${formatAmountForDisplay(freshCoffreBalance, false).text})`,
        severity: 'error'
      });
      return;
    }

    // Show confirmation dialog instead of directly creating
    setCreateConfirmDialog({ 
      open: true, 
      formData: formData 
    });
  };

  const handleConfirmCreateRequest = async () => {
    if (!createConfirmDialog.formData) return;
    
    setActionLoading(true);
    try {
      await withdrawalRequestAPI.create({
        caisseId: createConfirmDialog.formData.caisse,
        amount: parseFloat(createConfirmDialog.formData.amount),
        assignedToUserId: createConfirmDialog.formData.assignedTo,
        note: createConfirmDialog.formData.note
      });
      
      setSnackbar({ 
        open: true, 
        message: 'Demande créée avec succès', 
        severity: 'success' 
      });
      setCreateModal({ open: false });
      setCreateConfirmDialog({ open: false, formData: null });
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error creating request:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la création de la demande', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle field changes with smart logic
  const handleFieldChange = (fieldName, value, formData) => {
    const newFormData = { ...formData, [fieldName]: value };
    
    // Smart logic: When caisse changes, clear assignedTo if the current agent is not assigned to the new caisse
    if (fieldName === 'caisse') {
      const currentAgentId = formData.assignedTo;
      if (currentAgentId) {
        const agentsForNewCaisse = users.filter(user => {
          if (user.role !== 'AGENT') return false;
          
          // Handle different possible structures for assignedCaisses
          if (user.assignedCaisses && Array.isArray(user.assignedCaisses)) {
            return user.assignedCaisses.some(assignment => {
              const caisse = assignment.caisse || assignment;
              return caisse && caisse.id === value;
            });
          }
          
          // Fallback: check if user has a direct caisse property
          if (user.caisse && user.caisse.id === value) {
            return true;
          }
          
          return false;
        });
        
        const isCurrentAgentValid = agentsForNewCaisse.some(agent => agent.id === currentAgentId);
        if (!isCurrentAgentValid) {
          newFormData.assignedTo = ''; // Clear invalid agent selection
        }
      }
    }
    
    // Smart logic: When agent changes, auto-select their caisse
    if (fieldName === 'assignedTo') {
      const agent = users.find(user => user.id === value);
      if (agent) {
        // Handle different possible structures for assignedCaisses
        if (agent.assignedCaisses && Array.isArray(agent.assignedCaisses) && agent.assignedCaisses.length > 0) {
          const assignment = agent.assignedCaisses[0];
          const caisse = assignment.caisse || assignment;
          if (caisse && caisse.id) {
            newFormData.caisse = caisse.id;
          }
        } else if (agent.caisse && agent.caisse.id) {
          // Fallback: check if agent has a direct caisse property
          newFormData.caisse = agent.caisse.id;
        }
      }
    }
    
    return newFormData;
  };

  // Field definitions for creation modal - dynamic based on form data
  const createRequestFields = (formData) => {
    // Get assignable users (agents + managers) for the selected caisse
    const getAssignableUsersForCaisse = (caisseId) => {
      if (!caisseId) return [];
      return users.filter(user => {
        // Include both AGENT and MANAGER roles
        if (user.role !== 'AGENT' && user.role !== 'MANAGER') return false;
        
        // For managers, they can be assigned to any caisse (self-assignment)
        if (user.role === 'MANAGER') return true;
        
        // For agents, check if they're assigned to the selected caisse
        if (user.role === 'AGENT') {
          // Handle different possible structures for assignedCaisses
          if (user.assignedCaisses && Array.isArray(user.assignedCaisses)) {
            return user.assignedCaisses.some(assignment => {
              // Handle both direct caisse object and nested caisse object
              const caisse = assignment.caisse || assignment;
              return caisse && caisse.id === caisseId;
            });
          }
          
          // Fallback: check if user has a direct caisse property
          if (user.caisse && user.caisse.id === caisseId) {
            return true;
          }
        }
        
        return false;
      });
    };

    return [
      {
        name: 'caisse',
        label: 'Caisse',
        type: 'select',
        required: true,
        placeholder: 'Caisse',
        options: Array.isArray(caisses) ? caisses.map(caisse => ({ 
          value: caisse.id, 
          label: caisse.name 
        })) : []
      },
      {
        name: 'amount',
        label: 'Montant',
        type: 'number',
        required: true,
        placeholder: 'Montant'
      },
      {
        name: 'assignedTo',
        label: 'Assigner à',
        type: 'select',
        required: true,
        placeholder: 'Assigner à',
        options: getAssignableUsersForCaisse(formData.caisse).map(user => ({ 
          value: user.id, 
          label: `${user.firstName} ${user.lastName}` 
        }))
      },
      {
        name: 'note',
        label: 'Note',
        type: 'textarea',
        required: false,
        placeholder: 'Ajouter une note...'
      }
    ];
  };

  const handleConfirmBulkCancel = async () => {
    if (bulkCancelDialog.selectedIds.length === 0) return;
    
    setActionLoading(true);
    try {
      // Cancel all selected requests
      const cancelPromises = bulkCancelDialog.selectedIds.map(id => 
        withdrawalRequestAPI.cancel(id)
      );
      await Promise.all(cancelPromises);
      
      setSnackbar({ 
        open: true, 
        message: `${bulkCancelDialog.selectedIds.length} demande(s) annulée(s) avec succès`, 
        severity: 'success' 
      });
      setBulkCancelDialog({ open: false, selectedIds: [] });
      setSelectedRequests([]); // Clear selection
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error cancelling requests:', err);
      setSnackbar({ open: true, message: 'Erreur lors de l\'annulation des demandes', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmBulkValidate = async () => {
    if (bulkValidateDialog.selectedIds.length === 0) return;
    
    setActionLoading(true);
    try {
      // Validate all selected CONFIRMED requests
      const validatePromises = bulkValidateDialog.selectedIds.map(id => 
        withdrawalRequestAPI.validate(id)
      );
      
      await Promise.all(validatePromises);
      setSnackbar({ 
        open: true, 
        message: `${bulkValidateDialog.selectedIds.length} demande(s) validée(s) avec succès. Transactions Sortie Coffre créées.`, 
        severity: 'success' 
      });
      setBulkValidateDialog({ open: false, selectedIds: [] });
      setSelectedRequests([]);
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error bulk validating requests:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la validation des demandes', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Transform request data for Rows component
  const transformRequestData = (request) => {
    return {
      id: request.id,
      createdAt: request.createdAt, // Keep original for sorting
      date: request.createdAt ? new Date(request.createdAt).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : '',
      status: request.status || '',
      amount: request.amount ? formatAmountForDisplay(request.amount, false).text : '',
      Caisse: request.caisseName || '', // Match backend field name
      caisse: request.caisseName || '', // Keep for display compatibility
      assignedTo: request.assignedTo || '',
      requestedBy: request.requestedBy || '', // Match backend field name
      requestedById: request.requestedById || null, // Add ID field
      createdBy: request.requestedBy || '', // Keep for display compatibility
      note: request.note || '',
      requestCode: `REQ-${request.id}`, // Generate a code from ID
      // Store original data for details modal
      originalData: request
    };
  };

  // Define columns for Rows component
  const columns = [
    { 
      key: 'createdAt', 
      label: 'Date', 
      sortable: true, 
      width: '10%',
      render: (createdAt) => {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} - ${hours}:${minutes}`;
      }
    },
    { 
      key: 'status', 
      label: 'Statut', 
      sortable: true,
      width: '9%', // Status pill
      render: (status) => {
        const getStatusColor = (status) => {
          switch (status?.toUpperCase()) {
            case 'OPEN':
              return '#027BC6'; // Blue
            case 'CONFIRMED':
              return '#FF8C00'; // Orange
            case 'VALIDATED':
              return '#0AD69F'; // Green
            case 'CANCELLED':
              return '#EF456E'; // Red
            default:
              return '#919191'; // Gray fallback
          }
        };

        const getStatusText = (status) => {
          switch (status?.toUpperCase()) {
            case 'OPEN':
              return 'Ouvert';
            case 'CONFIRMED':
              return 'Confirmé';
            case 'VALIDATED':
              return 'Validé';
            case 'CANCELLED':
              return 'Annulé';
            default:
              return status || 'N/A';
          }
        };

        const statusColor = getStatusColor(status);
        const statusText = getStatusText(status);

        return (
          <StatusPill
            text={statusText}
            color={statusColor}
          />
        );
      }
    },
    { 
      key: 'amount', 
      label: 'Montant', 
      sortable: true,
      width: '12%', // Amount column
      render: (amount) => (
        <span style={{ 
          color: '#027BC6',
          fontFamily: 'Montserrat',
          fontSize: '16px',
          fontWeight: 'bold',
          lineHeight: '19px',
          letterSpacing: '0px',
                textTransform: 'none',
          opacity: 1
        }}>
          {amount}
        </span>
      )
    },
    { key: 'Caisse', label: 'Caisse', sortable: true, width: '10%' }, // Caisse column
    { key: 'assignedTo', label: 'Assigné à', sortable: true, width: '12%' }, // Assigned column
    { key: 'requestedBy', label: 'Créé par', sortable: true, width: '12%' }, // Created column
    { 
      key: 'note', 
      label: 'Note', 
      sortable: false,
      width: '18%', // Note column
      render: (note) => {
        if (!note || note.trim() === '') {
          return <span style={{ color: '#919191', fontStyle: 'italic' }}>Aucune note</span>;
        }
        
        const truncatedNote = note.length > 20 ? note.substring(0, 20) + '...' : note; // Shorter for single line
        
        return (
          <Tooltip
            title={
              <Box
                sx={{
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '23px',
                  letterSpacing: '0px',
                  textTransform: 'none',
                  opacity: 1,
                  maxWidth: '279px',
                  wordWrap: 'break-word'
                }}
              >
                {note}
              </Box>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#000000',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  '& .MuiTooltip-arrow': {
                    color: '#000000'
                  }
                }
              },
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8], // Adjust vertical offset
                    },
                  },
                ],
              }
            }}
            arrow
            placement="top"
            PopperProps={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                  },
                },
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top', 'bottom'],
                  },
                },
              ],
            }}
          >
            <span
              style={{
                cursor: 'pointer',
                color: '#333',
                fontSize: '16px',
                whiteSpace: 'nowrap', // Single line
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                maxWidth: '100%',
                textAlign: 'left'
              }}
            >
              {truncatedNote}
            </span>
          </Tooltip>
        );
      }
    },
    { 
      key: 'actions', 
      label: 'Actions', 
      sortable: false,
      width: '15%', // Actions column
      render: (value, row) => {
        const status = row.originalData?.status;
        
        // For AGENT users - only show Confirm button for OPEN requests
        if (user?.role === 'AGENT') {
          if (status === 'OPEN') {
            return (
              <Button
                variant="contained"
                size="small"
                sx={{
                  width: '100%',
                  maxWidth: '90px',
                  height: '28px',
                  backgroundColor: '#FF8C00', // Orange color for CONFIRMED status
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0px 6px 26px #626B7D12',
                  borderRadius: '12px',
                  opacity: 1,
                  '&:hover': {
                    backgroundColor: '#E67E00' // Darker orange on hover
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmRequest(row.id, row.requestCode, row.originalData.amount);
                }}
              >
                Confirmer
              </Button>
            );
          }
          // No actions for other statuses for agents
          return null;
        }
        
        // For MANAGER users - show Cancel for OPEN, Validate for CONFIRMED
        // But only for requests they created themselves
        const isCreator = row.originalData?.requestedById === user?.id;
        
        if (!isCreator) {
          // No actions for requests created by other managers
          return null;
        }
        
        if (status === 'OPEN') {
          return (
            <Button
              variant="contained"
              size="small"
              sx={{
                width: '100%',
                maxWidth: '90px',
                height: '31px',
                backgroundColor: '#EF456E',
                color: 'white',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0px 6px 26px #626B7D12',
                borderRadius: '12px',
                opacity: 1,
                '&:hover': {
                  backgroundColor: '#D63A5F'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCancelRequest(row.id, row.requestCode);
              }}
            >
              Annuler
            </Button>
          );
        }
        
        if (status === 'CONFIRMED') {
          return (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  width: '100%',
                  maxWidth: '80px',
                  height: '28px',
                  backgroundColor: '#0AD69F',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0px 6px 26px #626B7D12',
                  borderRadius: '12px',
                  opacity: 1,
                  '&:hover': {
                    backgroundColor: '#08B894'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleValidateRequest(row.id, row.requestCode, row.originalData.amount);
                }}
              >
                Valider
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  width: '100%',
                  maxWidth: '90px',
                  height: '28px',
                  backgroundColor: '#EF456E',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0px 6px 26px #626B7D12',
                  borderRadius: '12px',
                  opacity: 1,
                  '&:hover': {
                    backgroundColor: '#D63A5F'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelRequest(row.id, row.requestCode);
                }}
              >
                Annuler
              </Button>
            </Box>
          );
        }
        
        return null;
      }
    }
  ];

  const transformedRequests = requests.map(transformRequestData);

  // Selection logic for header checkbox
  const selectableRows = user?.role === 'MANAGER' ? transformedRequests.filter(req => canSelectRequest(req)) : [];
  const isAllSelected = user?.role === 'MANAGER' && selectableRows.length > 0 && selectedRequests.length === selectableRows.length;
  const isIndeterminate = user?.role === 'MANAGER' && selectedRequests.length > 0 && selectedRequests.length < selectableRows.length;

  const handleSelectAll = () => {
    if (user?.role !== 'MANAGER') return;
    if (isAllSelected) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(selectableRows.map(row => row.id));
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        className="requests-sticky-header"
        sx={{
          position: 'sticky',
          top: '0px',
          zIndex: 1000,
          backgroundColor: isStuck ? '#F8F8F8' : 'transparent',
          borderBottom: isStuck ? '1px solid #E5E8EF' : 'none',
          transition: 'background-color 0.2s ease, border-bottom 0.2s ease'
        }}
      >
        <Box sx={stickyOuterSx}>
          <Box sx={{ paddingTop: '24px', paddingBottom: '8px' }}>
            {/* Page Title and Create Button */}
            <Box sx={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '28px',
                  lineHeight: '32px',
                  letterSpacing: '0px',
                  color: '#38485C',
                  opacity: 0.88,
                  textTransform: 'none',
                  margin: 0
                }}
              >
                Demandes
              </h1>
              {user?.role === 'MANAGER' && (
                <Button
                  variant="contained"
                  startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
                  onClick={handleCreateRequest}
                  sx={{
                    height: '40px',
                    backgroundColor: '#027BC6',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    opacity: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#025a9c',
                    },
                  }}
                >
                  Créer une demande
                </Button>
              )}
            </Box>

            {/* Filters */}
            <Box sx={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 1001 }}>
              <WithdrawalRequestFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
                userRole={user?.role}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat'
            }}
          >
            {user?.role === 'MANAGER' && (
              <Box sx={{ flex: 'none', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '1px solid #C5C5C5',
                    borderRadius: '4px',
                    opacity: 1,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundColor: isAllSelected || isIndeterminate ? '#007BC6' : 'white',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!isAllSelected && !isIndeterminate) {
                      e.target.style.borderColor = '#007BC6';
                      e.target.style.backgroundColor = '#F0F8FF';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isAllSelected && !isIndeterminate) {
                      e.target.style.borderColor = '#C5C5C5';
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                />
                {isAllSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '14px',
                      height: '14px',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={checkIcon}
                      alt="check"
                      style={{
                        width: '10px',
                        height: '10px',
                        filter: 'brightness(0) invert(1) drop-shadow(0 0 0.5px white)'
                      }}
                    />
                  </Box>
                )}
                {isIndeterminate && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '8px',
                      height: '2px',
                      backgroundColor: 'white',
                      borderRadius: '1px',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </Box>
            )}
            
            {columns.map((column, index) => {
              const isActionsColumn = column.key === 'actions';
              const isSortable = column.sortable && handleSort;
              const isCurrentlySorted = sortBy === column.key;
              
              return (
                <Box
                  key={index}
                  sx={{
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    textAlign: isActionsColumn ? 'center' : 'left',
                    ...(isActionsColumn 
                      ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                      : column.key === 'note'
                        ? {}
                        : column.key === 'amount'
                          ? { flex: 'none', width: '200px' }
                          : column.key === 'status'
                            ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                            : column.width
                              ? { flex: 'none', width: column.width }
                              : { flex: 1, minWidth: '150px' }),
                    ...(isActionsColumn && column.width ? { width: column.width, flexShrink: 0, flexGrow: 0 } : {}),
                    ...(column.width && !isActionsColumn ? { width: column.width } : {})
                  }}
                >
                  {isSortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                        color: isCurrentlySorted ? '#027BC6' : '#919191'
                      }}
                      onClick={() => {
                        let newDirection = 'ASC';
                        if (sortBy === column.key && sortDirection === 'ASC') {
                          newDirection = 'DESC';
                        }
                        handleSort(column.key, newDirection);
                      }}
                    >
                      <span>{column.label}</span>
                      {isCurrentlySorted && (
                        <img
                          src={sortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon}
                          alt={sortDirection === 'ASC' ? 'ascending' : 'descending'}
                          style={{
                            width: '12px',
                            height: '12px',
                            filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    column.label
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={contentContainerSx}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Bulk Actions Bar */}
        {user?.role === 'MANAGER' && selectedRequests.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F8F9FA',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #E9ECEF'
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: '#495057'
              }}
            >
              {selectedRequests.length} demande(s) sélectionnée(s)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Show Cancel button if there are OPEN requests selected that the user created */}
              {transformedRequests.some(request => 
                selectedRequests.includes(request.id) && 
                request.originalData?.status === 'OPEN' &&
                request.originalData?.requestedById === user?.id
              ) && (
                <Button
                  onClick={handleBulkCancel}
                  sx={{
                    width: '120px',
                    height: '28px',
                    backgroundColor: '#EF456E',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0px 6px 26px #626B7D12',
                    borderRadius: '12px',
                    opacity: 1,
                    '&:hover': {
                      backgroundColor: '#D63A5F'
                    }
                  }}
                >
                  Annuler tout
                </Button>
              )}
              
              {/* Show Validate button if there are CONFIRMED requests selected that the user created */}
              {transformedRequests.some(request => 
                selectedRequests.includes(request.id) && 
                request.originalData?.status === 'CONFIRMED' &&
                request.originalData?.requestedById === user?.id
              ) && (
                <Button
                  onClick={handleBulkValidate}
                  sx={{
                    width: '120px',
                    height: '28px',
                    backgroundColor: '#0AD69F',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0px 6px 26px #626B7D12',
                    borderRadius: '12px',
                    opacity: 1,
                    '&:hover': {
                      backgroundColor: '#08B894'
                    }
                  }}
                >
                  Valider tout
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Request List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Rows
            columns={columns}
            data={transformedRequests}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total
            }}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            selectable={user?.role === 'MANAGER'}
            selectedRows={selectedRequests}
            onSelectionChange={setSelectedRequests}
            canSelectRow={canSelectRequest}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            title={null}
            wrapLayout={false}
            expandable={false}
            hideHeader={true}
            containerPadding={0}
          />
        )}
      </Box>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, requestId: null, requestCode: null })}
        onConfirm={handleConfirmCancel}
        type="cancel"
        message="Êtes-vous sûr de vouloir annuler cette demande ?"
        confirmText="Annuler"
        cancelText="Retour"
        loading={actionLoading}
      />

      <ConfirmationDialog
        open={validateDialog.open}
        onClose={() => setValidateDialog({ open: false, requestId: null, requestCode: null, amount: null })}
        onConfirm={handleConfirmValidate}
        type="validate"
        messageComponent={
          <Typography sx={{ fontSize: '18px', color: '#374151', textAlign: 'center', fontFamily: 'Montserrat', fontWeight: 500 }}>
            Êtes-vous sûr de vouloir valider cette demande ? Cela créera une transaction Sortie Coffre de{' '}
            <span style={{ color: '#007BC6', fontWeight: '600' }}>
              {formatAmountForDisplay(validateDialog.amount, false).text}
            </span>
            .
          </Typography>
        }
        confirmText="Valider"
        cancelText="Retour"
        loading={actionLoading}
      />

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, requestId: null, requestCode: null, amount: null })}
        onConfirm={handleConfirmConfirm}
        type="confirm"
        messageComponent={
          <Typography sx={{ fontSize: '18px', color: '#374151', textAlign: 'center', fontFamily: 'Montserrat', fontWeight: 500 }}>
            Êtes-vous sûr de vouloir confirmer cette demande de{' '}
            <span style={{ color: '#007BC6', fontWeight: '600' }}>
              {formatAmountForDisplay(confirmDialog.amount, false).text}
            </span>
            {' '}?
          </Typography>
        }
        confirmText="Confirmer"
        cancelText="Retour"
        loading={actionLoading}
      />

      <ConfirmationDialog
        open={bulkCancelDialog.open}
        onClose={() => setBulkCancelDialog({ open: false, selectedIds: [] })}
        onConfirm={handleConfirmBulkCancel}
        type="cancel"
        message={`Êtes-vous sûr de vouloir annuler ${bulkCancelDialog.selectedIds.length} demande(s) ?`}
        confirmText="Annuler tout"
        cancelText="Retour"
        loading={actionLoading}
      />

      <ConfirmationDialog
        open={bulkValidateDialog.open}
        onClose={() => setBulkValidateDialog({ open: false, selectedIds: [] })}
        onConfirm={handleConfirmBulkValidate}
        type="validate"
        message={`Êtes-vous sûr de vouloir valider ${bulkValidateDialog.selectedIds.length} demande(s) ? Cela créera des transactions Sortie Coffre.`}
        confirmText="Valider tout"
        cancelText="Retour"
        loading={actionLoading}
      />

      <ConfirmationDialog
        open={createConfirmDialog.open}
        onClose={() => setCreateConfirmDialog({ open: false, formData: null })}
        onConfirm={handleConfirmCreateRequest}
        type="create"
        messageComponent={
          <Typography sx={{ fontSize: '18px', color: '#374151', textAlign: 'center', fontFamily: 'Montserrat', fontWeight: 500 }}>
            Êtes-vous sûr de vouloir créer une demande de retrait de{' '}
            <span style={{ color: '#007BC6', fontWeight: '600' }}>
              {createConfirmDialog.formData ? formatAmountForDisplay(parseFloat(createConfirmDialog.formData.amount), false).text : ''}
            </span>
            {' '}pour{' '}
            <span style={{ color: '#007BC6', fontWeight: '600' }}>
              {createConfirmDialog.formData ? caisses.find(c => c.id === createConfirmDialog.formData.caisse)?.name : ''}
            </span>
            {' '}?
          </Typography>
        }
        confirmText="Créer"
        cancelText="Retour"
        loading={actionLoading}
      />

      {/* Creation Modal */}
              <CreationModal
                open={createModal.open}
                onClose={() => setCreateModal({ open: false })}
                onSubmit={handleSubmitCreateRequest}
                title="Créer une demande"
                fields={createRequestFields}
                submitButtonText="Créer une nouvelle demande"
                loading={actionLoading}
                initialData={useMemo(() => ({ 
                  caisse: '', 
                  amount: '', 
                  assignedTo: '', 
                  note: '' 
                }), [])}
                onFieldChange={handleFieldChange}
              />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WithdrawalRequests;